import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  List,
  ListItemText,
  ListItemButton,
  Divider,
} from "@mui/material";
import Header from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import { privacyPolicySections } from "./privacyData";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("introduction");
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-15% 0px -70% 0px",
      }
    );

    privacyPolicySections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => {
      privacyPolicySections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) observer.unobserve(el);
      });
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const scrollToSection = (id: string) => {
    isScrollingRef.current = true;
    setActiveSection(id);

    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 120;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 800);
  };

  return (
    <Box
      sx={{
        maxWidth: "2000px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Header />
      <Box sx={{ flex: 1 }}>
        <Box>
          <Box
            sx={{
              background: "linear-gradient(180deg, #FAF8F5 0%, #F5EFEB 100%)",
              color: "#161717",
              py: { xs: 4, md: 5 },
              px: { xs: 1.5, sm: 3, md: 6.4 },
              textAlign: "center",
              borderBottom: "1px solid rgba(137, 101, 42, 0.15)",
            }}
          >
            <Container maxWidth="xl">
              <Typography
                variant="h1"
                sx={{
                  fontFamily: "League Spartan",
                  fontSize: { xs: "34px", md: "48px" },
                  fontWeight: 700,
                  letterSpacing: "-0.5px",
                  mb: 1.5,
                  color: "#161717",
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                sx={{
                  fontFamily: "League Spartan",
                  fontSize: { xs: "16px", md: "18px" },
                  color: "#89652A",
                  fontWeight: 500,
                }}
              >
                Last Updated: 11-Jun-2026
              </Typography>
            </Container>
          </Box>

          <Container
            maxWidth="xl"
            sx={{
              py: { xs: 3, md: 4 },
              px: { xs: 1.5, sm: 3, md: 4 },
            }}
          >
            <Grid container spacing={4}>
              <Grid
                item
                md={3}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                <Box
                  sx={{
                    position: "sticky",
                    top: "120px",
                    maxHeight: "calc(100vh - 160px)",
                    overflowY: "auto",
                    pr: 2,
                    py: 1,
                    borderRight: "1px solid rgba(182, 157, 116, 0.2)",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(182, 157, 116, 0.3)",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#89652A",
                    },
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {privacyPolicySections.map((section) => {
                      const isActive = activeSection === section.id;
                      return (
                        <ListItemButton
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          sx={{
                            py: 1,
                            pl: 2,
                            borderLeft: `3px solid ${
                              isActive ? "#89652A" : "transparent"
                            }`,
                            backgroundColor: isActive
                              ? "rgba(137, 101, 42, 0.04)"
                              : "transparent",
                            borderRadius: "0 8px 8px 0",
                            "&:hover": {
                              color: "#89652A",
                              backgroundColor: "rgba(137, 101, 42, 0.02)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={section.title.split(". ")[1]}
                            primaryTypographyProps={{
                              fontFamily: "League Spartan",
                              fontSize: "15px",
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? "#89652A" : "#555555",
                              lineHeight: 1.3,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Box>
              </Grid>

              <Grid item xs={12} md={9}>
                <Box sx={{ pl: { md: 4 } }}>
                  {privacyPolicySections.map((section, idx) => (
                    <Box
                      key={section.id}
                      id={section.id}
                      sx={{
                        mb: 5,
                        scrollMarginTop: "140px",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontFamily: "League Spartan",
                          fontSize: { xs: "24px", md: "30px" },
                          fontWeight: 700,
                          color: "#161717",
                          mb: 2,
                          pb: 0.5,
                          display: "inline-block",
                          position: "relative",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "40px",
                            height: "3px",
                            backgroundColor: "#89652A",
                          },
                        }}
                      >
                        {section.title}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        {section.blocks.map((block, bIdx) => {
                          switch (block.type) {
                            case "paragraph":
                              return (
                                <Typography
                                  key={bIdx}
                                  sx={{
                                    fontFamily: "Roboto",
                                    fontSize: "16.5px",
                                    lineHeight: "1.75",
                                    color: "#3A3A3A",
                                    mb: 2,
                                  }}
                                >
                                  {block.text}
                                </Typography>
                              );

                            case "subheading":
                              return (
                                <Typography
                                  key={bIdx}
                                  variant="h3"
                                  sx={{
                                    fontFamily: "League Spartan",
                                    fontSize: { xs: "18px", md: "21px" },
                                    fontWeight: 600,
                                    color: "#89652A",
                                    mt: 3,
                                    mb: 1.5,
                                  }}
                                >
                                  {block.title}
                                </Typography>
                              );

                            case "list":
                              return (
                                <Box
                                  component="ul"
                                  key={bIdx}
                                  sx={{
                                    pl: 2.5,
                                    mb: 2.5,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {block.items?.map((item, itemIdx) => (
                                    <Box
                                      component="li"
                                      key={itemIdx}
                                      sx={{
                                        fontFamily: "Roboto",
                                        fontSize: "16.5px",
                                        lineHeight: "1.65",
                                        color: "#3A3A3A",
                                      }}
                                    >
                                      {item}
                                    </Box>
                                  ))}
                                </Box>
                              );

                            case "contact-box":
                              return (
                                <Box
                                  key={bIdx}
                                  sx={{
                                    p: { xs: 3, md: 4 },
                                    borderRadius: "12px",
                                    backgroundColor: "#FDFCFB",
                                    borderLeft: "4px solid #89652A",
                                    borderTop: "1px solid rgba(182, 157, 116, 0.15)",
                                    borderRight: "1px solid rgba(182, 157, 116, 0.15)",
                                    borderBottom: "1px solid rgba(182, 157, 116, 0.15)",
                                    mt: 2.5,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: "League Spartan",
                                      fontSize: "20px",
                                      fontWeight: 700,
                                      color: "#161717",
                                      mb: 1.5,
                                    }}
                                  >
                                    {block.companyName}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: "Roboto",
                                      fontSize: "16px",
                                      lineHeight: "1.7",
                                      color: "#3A3A3A",
                                      mb: 1,
                                    }}
                                  >
                                    <strong>Email:</strong>{" "}
                                    <a
                                      href={`mailto:${block.email}`}
                                      style={{
                                        color: "#89652A",
                                        textDecoration: "none",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {block.email}
                                    </a>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: "Roboto",
                                      fontSize: "16px",
                                      lineHeight: "1.7",
                                      color: "#3A3A3A",
                                      mb: 1,
                                    }}
                                  >
                                    <strong>Website:</strong>{" "}
                                    <a
                                      href={block.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#89652A",
                                        textDecoration: "none",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {block.website}
                                    </a>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: "Roboto",
                                      fontSize: "16px",
                                      lineHeight: "1.7",
                                      color: "#3A3A3A",
                                    }}
                                  >
                                    {block.address}
                                  </Typography>
                                </Box>
                              );

                            default:
                              return null;
                          }
                        })}
                      </Box>

                      {idx < privacyPolicySections.length - 1 && (
                        <Divider
                          sx={{
                            mt: 4.5,
                            borderColor: "rgba(182, 157, 116, 0.15)",
                            borderWidth: "1px",
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
